import {
  useAssignShiftToTeam,
  useRemoveTeamShift,
  useShifts,
  useTeamShifts,
} from '@/domains/shifts';
import { TeamDetailView } from '@/domains/teams/components/TeamDetailView';
import {
  useAddTeamMember,
  useRemoveTeamMember,
  useTeam,
  useUpdateTeam,
} from '@/domains/teams/hooks/useTeams';
import { useUsers, userListItemToView } from '@/domains/users';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

export function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();

  const id = teamId ? Number(teamId) : null;
  const { data: team, isLoading } = useTeam(id);
  const addMember = useAddTeamMember(id ?? 0);
  const removeMember = useRemoveTeamMember(id ?? 0);
  const updateTeam = useUpdateTeam(id ?? 0);
  const { data: usersData } = useUsers({ status: 'active', limit: '1000' });
  const { data: shiftsData } = useShifts();
  const { data: teamShiftsData } = useTeamShifts(id);
  const assignShiftToTeam = useAssignShiftToTeam();
  const removeTeamShift = useRemoveTeamShift();

  const allUsers = useMemo(() => usersData?.users?.map(userListItemToView) ?? [], [usersData]);
  const allShifts = useMemo(() => shiftsData?.values ?? [], [shiftsData]);
  const assignedShifts = useMemo(() => teamShiftsData ?? [], [teamShiftsData]);

  if (!teamId) return null;

  return (
    <TeamDetailView
      team={team ?? null}
      isLoading={isLoading}
      onBack={() => navigate('/teams')}
      allUsers={allUsers}
      allShifts={allShifts}
      assignedShifts={assignedShifts}
      onAddMembers={async (userIds) => {
        for (const userId of userIds) {
          await addMember.mutateAsync({ userId });
        }
      }}
      onRemoveMember={async (userId, newTeamLeadId) => {
        await removeMember.mutateAsync({ userId, newTeamLeadId });
      }}
      onUpdateTeam={async (data) => {
        await updateTeam.mutateAsync(data);
      }}
      onAssignShift={async (shiftId, startDate, endDate) => {
        await assignShiftToTeam.mutateAsync({
          shiftId,
          teamId: id!,
          startDate,
          endDate,
        });
      }}
      onRemoveShift={async (shiftId, startDate, endDate) => {
        await removeTeamShift.mutateAsync({
          shiftId,
          teamId: id!,
          startDate,
          endDate,
        });
      }}
    />
  );
}
